import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  EmailAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  multiFactor,
  reauthenticateWithCredential,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from '../constants/firebaseModular';
import { requestEnrollAuthorization } from '../data/mfa/requestEnrollAuthorization';
import { formatPhoneE164 } from '../utils/phone';
import { isRegionAllowed, isRegionRejection, isUnverifiedEmailRejection, isMalformedPhoneError } from '../utils/mfa/regionPolicy';
import { scrubSafeAuthCode } from '../utils/mfa/sentryAuthCode';
import { browserReportsOnline, isNetworkError } from '../utils/mfa/networkError';
import { flushOfflineFailures, noteOfflineFailure } from '../utils/mfa/offlineFailureReport';

/**
 * SMS second-factor enrollment for a PATIENT — CU-86ajwpnek §1.
 *
 * Ported from the admin console's `useAdminSmsEnrollment` (CU-86ajva3wz). The
 * ceremony is identical; the copy, the phone handling and the region layers are
 * not. See `docs/projects/CU-86ajwpnek/CU-86ajwpnek_IMPLEMENTATION_PLAN.md` §5.
 *
 * ── THE RAIL — THIS IS THE WHOLE FEATURE ──────────────────────────────────
 * `multiFactor().enroll()` goes client→Identity Platform directly, and Firebase
 * exposes NO enroll-time blocking trigger. So the C3 authorization cannot
 * PREVENT a factor being planted; it is the evidence the reconciliation sweep
 * matches against. A factor that reaches IdP without one is flagged
 * `reconcile.unmatched-factor` — the attacker's signature — on a real patient's
 * account, indefinitely.
 *
 * Three redundant links keep that from happening by accident:
 *
 *   1. THE STEP MACHINE. `setStep('phone')` is the LAST statement on
 *      `submitPassword`'s success path, so any throw from the mint lands in the
 *      catch and the ceremony never advances. This — not the verification id —
 *      is the primary enforcement.
 *   2. `verificationIdRef` — no enroll without an OTP we actually dispatched.
 *   3. `authorizationMintedRef` — an explicit latch, asserted immediately
 *      before `enroll()` and again on entry to `submitPhone`.
 *
 * ⚠️ Tests must assert ORDER, not occurrence. "Enrolled THEN minted" produces an
 * unmatched factor just as surely as never minting, and it passes a test that
 * only checks both were called. The console found that by mutation-testing.
 *
 * ── WHAT DIFFERS FROM THE CONSOLE ─────────────────────────────────────────
 * • Phone input uses `formatPhoneE164(value, country)` (CU-86ajrx60c) rather
 *   than the console's hand-rolled North-America-only `toE164`, so a number
 *   typed the way a patient dials it locally (AU `0412345678`) resolves and
 *   REACHES the region check instead of being assumed US and rejected as
 *   malformed.
 * • Both region layers are implemented (the console has neither — its
 *   population is overwhelmingly US). `region-not-supported` is a distinct
 *   error kind here.
 * • `RecaptchaVerifier` is cleared on unmount. The console leaks it.
 * • All copy is patient-facing. The console's says "admin" throughout and
 *   references the Firebase console, which patients do not have.
 *
 * ── PHI DISCIPLINE (M3) ───────────────────────────────────────────────────
 * ⚠️ NEVER send the phone number, the OTP, or the verification id to Sentry.
 * The last two are credentials. Codes, messages and counts only. `message` is
 * carried alongside `code` because the code alone can be worthless — the
 * verified-email gate arrives as `auth/unknown` with the only real signal in
 * the prose. IdP messages describe policy, not people.
 */

/** Where the ceremony is. Forward-only; `reset()` is the only way back. */
export type SmsEnrollmentStep = 'reauth' | 'phone' | 'code' | 'done';

export type SmsEnrollmentError =
  /** The account already holds a factor. NOT a failure of the password. */
  | { kind: 'already-enrolled' }
  | { kind: 'bad-password' }
  /** The reauth proof aged out mid-ceremony, or the session went stale. */
  | { kind: 'stale-session' }
  | { kind: 'invalid-phone' }
  /** The number is well-formed but its region is not enabled for SMS. */
  | { kind: 'region-not-supported' }
  /** Identity Platform refuses enrollment until the account email is verified. */
  | { kind: 'unverified-email' }
  | { kind: 'too-many-requests' }
  | { kind: 'resend-cap-reached' }
  | { kind: 'send-failed' }
  | { kind: 'bad-code' }
  /**
   * The request never reached the server (CU-86akbg4qm). Any step can raise
   * it, so the section shows it unscoped. Set only from `isNetworkError`.
   *
   * ⚠️ MEASURED 2026-09-28 (probe 4b): an unreachable mint arrived as
   * `functions/internal` — the SAME code a server 500 produces — and showed
   * `generic`. That code is therefore offline only when `navigator.onLine` is
   * also false; see `utils/mfa/networkError.ts`.
   */
  | { kind: 'offline' }
  | { kind: 'generic' };

export interface UseSmsEnrollment {
  step: SmsEnrollmentStep;
  busy: boolean;
  error: SmsEnrollmentError | null;
  /** Layer 1 advisory: the number parses, but its region looks unsupported. */
  regionWarning: boolean;
  submitPassword: (_password: string) => Promise<boolean>;
  submitPhone: (_phone: string, _country?: string | null) => Promise<boolean>;
  submitCode: (_code: string) => Promise<boolean>;
  /**
   * Re-send the account verification email. Only meaningful while `error` is
   * `unverified-email`.
   *
   * ⚠️ The Firebase console has NO admin-side "send verification email" action,
   * so the client is the ONLY way a patient can unblock themselves. Without
   * this, a patient whose original email never arrived has no route forward at
   * all — not even through support. Resolves true if the send succeeded.
   */
  resendVerificationEmail: () => Promise<boolean>;
  /**
   * Dismiss the current error.
   *
   * ⚠️ The code step needs this on EDIT. Without it a `bad-code` rejection sits
   * on screen while the patient types a corrected code, and they conclude the
   * new code failed too — measured on mobile 2026-07-29, and again on the
   * portal 2026-08-07 (§A3). `checkRegion` already does this for the phone
   * step; the code step has no equivalent hook call, hence this.
   */
  clearError: () => void;
  /** Layer 1 check, for onBlur. Also clears the current error. */
  checkRegion: (_phone: string, _country?: string | null) => void;
  /**
   * Step back from the code step to the phone step, KEEPING the minted
   * authorization.
   *
   * ⚠️ Deliberately NOT `reset()`. A patient who mistyped one digit has a valid
   * authorization and a cleared password step; sending them all the way back
   * would discard both and make them re-enter their password — and it would
   * burn another mint against the 5-per-300s limiter for no reason.
   */
  changeNumber: () => void;
  reset: () => void;
  resendCooldownSecs: number;
  resendCapReached: boolean;
  /** @internal test seam — see the note at the return statement. */
  __clearAuthorizationLatchForTest: () => void;
}

/**
 * Test seam. Production passes nothing; tests inject fakes rather than mocking
 * module internals, so the ceremony under test is the real one.
 */
export interface SmsEnrollmentDeps {
  makeVerifier?: (_containerId: string) => { clear?: () => void };
  reauthenticate?: (_password: string) => Promise<void>;
  reloadUser?: () => Promise<void>;
  isEmailVerified?: () => boolean;
  sendVerificationEmail?: () => Promise<void>;
  getReauthProof?: () => Promise<string>;
  mintAuthorization?: (_reauthProof: string) => Promise<unknown>;
  getSession?: () => Promise<unknown>;
  verifyPhoneNumber?: (_options: unknown, _verifier: unknown) => Promise<string>;
  buildAssertion?: (_verificationId: string, _code: string) => unknown;
  enroll?: (_assertion: unknown, _displayName: string) => Promise<void>;
  now?: () => number;
}

/** Escalating waits, indexed by resends already made. */
const RESEND_COOLDOWNS_MS = [30_000, 60_000, 120_000];
const RESEND_CAP = 5;

/** IdP's label for the enrolled factor. Non-identifying by design (M3). */
const FACTOR_DISPLAY_NAME = 'mobile number';

export function useSmsEnrollment(containerId: string, deps: SmsEnrollmentDeps = {}): UseSmsEnrollment {
  const [step, setStep] = useState<SmsEnrollmentStep>('reauth');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<SmsEnrollmentError | null>(null);
  const [regionWarning, setRegionWarning] = useState(false);

  /**
   * ⚠️ LINK 3 OF THE RAIL. Set ONLY after `requestEnrollAuthorization` resolves;
   * asserted immediately before `enroll()`. Never set optimistically, never set
   * in a `finally`, and never restored on a retry that did not re-mint.
   */
  const authorizationMintedRef = useRef(false);
  /** ⚠️ LINK 2. Ties the dispatched OTP to the code typed back. */
  const verificationIdRef = useRef<string | null>(null);
  const verifierRef = useRef<{ clear?: () => void } | null>(null);
  const sendCountRef = useRef(0);
  /** The cap signal is sent ONCE per ceremony — the send buttons stay pressable at the cap. */
  const capReportedRef = useRef(false);
  const [resendAllowedAt, setResendAllowedAt] = useState(0);
  const [tick, setTick] = useState(0);

  // ⚠️ Memoized on `deps.now`, not recreated inline. The inline form builds a
  // new closure every render, which churns `submitPhone`'s identity through its
  // dependency array — so a parent that passes it to a memoized child defeats
  // the memo, and any effect keyed on it re-runs each render. The console has
  // the inline form and eslint flags it there too.
  const nowRef = useRef(deps.now);
  nowRef.current = deps.now;
  const now = useCallback(() => (nowRef.current ?? Date.now)(), []);

  // Re-render once per second while a cooldown is pending, so the countdown
  // actually counts down. Idle when there is nothing to count.
  // Stops once the cooldown elapses (CU-86ajtgauu): `resendAllowedAt` never
  // returns to 0, so an interval that only stopped on unmount re-rendered this
  // form every second for as long as it stayed open.
  useEffect(() => {
    if (resendAllowedAt === 0 || now() >= resendAllowedAt) return undefined;
    const id = setInterval(() => {
      setTick((t) => t + 1);
      if (now() >= resendAllowedAt) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [resendAllowedAt, now]);

  const msRemaining = Math.max(0, resendAllowedAt - now());
  // `tick` is read so this re-evaluates each second; the value itself is unused.
  void tick;
  const resendCooldownSecs = Math.ceil(msRemaining / 1000);
  const resendCapReached = sendCountRef.current >= RESEND_CAP;

  /**
   * Tear down the current verifier AND empty its container.
   *
   * ⚠️ `clear()` ALONE IS NOT ENOUGH FOR AN INVISIBLE VERIFIER. Firebase's
   * implementation only removes the widget's DOM when the verifier is VISIBLE:
   *
   *     if (!this.isInvisible) {
   *       this.container.childNodes.forEach(n => this.container.removeChild(n));
   *     }
   *
   * We use `size: 'invisible'`, so `clear()` marks the instance destroyed and
   * leaves the widget in place. The NEXT `render()` into that same container
   * throws `reCAPTCHA has already been rendered in this element`,
   * app-verification never completes, and Identity Platform rejects the
   * enrollment start with a 400.
   *
   * ⚠️ EMPTYING THE CONTAINER IS NOT ENOUGH — measured, twice. Look at what
   * Firebase does on the render path:
   *
   *     if (!this.isInvisible) {
   *       const guaranteedEmpty = document.createElement('div');
   *       container.appendChild(guaranteedEmpty);
   *       container = guaranteedEmpty;          // ← visible: a FRESH node
   *     }
   *     this.widgetId = this.getAssertedRecaptcha().render(container, ...);
   *
   * A VISIBLE verifier gets a brand-new child div every time — that is what
   * "guaranteedEmpty" means. An INVISIBLE one renders straight into OUR
   * container, and Google's `grecaptcha` then marks that ELEMENT as occupied.
   * The marker lives inside Google's script, not in the DOM we own, so
   * `innerHTML = ''` does not clear it. A first fix that emptied the container
   * still threw (MIDEO-UNIFIED-8W, release `25010c8`).
   *
   * So we do for ourselves what Firebase does for the visible case: REPLACE the
   * container element with a fresh one carrying the same id. A new element has
   * never been rendered into, so Google's marker cannot apply to it.
   *
   * ⚠️ MEASURED ON STAGING 2026-08-07 (Safari 26.6) on the second send. The
   * admin console has the same defect — it simply rarely retries a send.
   */
  const teardownVerifier = useCallback(() => {
    try {
      verifierRef.current?.clear?.();
    } catch {
      // A verifier can already be destroyed (double teardown, or a failed
      // render). That is not a reason to skip replacing the node below.
    }
    verifierRef.current = null;

    if (typeof document === 'undefined') return;
    const stale = document.getElementById(containerId);
    if (!stale?.parentNode) return;

    // ⚠️ DO NOT REMOVE THE NODE SYNCHRONOUSLY. reCAPTCHA schedules a 500ms
    // delayed hide animation that reaches back into its badge element:
    //
    //     p[48](2, function(){ K[44](15, this.g, "top", "-10000px") }, 500, …)
    //
    // Removing the container first makes `this.g` null when that timer fires,
    // and Google's own callback throws `Cannot read properties of null
    // (reading 'style')` — unhandled, because it is inside their code.
    // MEASURED on staging 2026-08-07 (MIDEO-UNIFIED-8Y, Chrome 151).
    //
    // Harmless in itself — a fire-and-forget animation on a widget nobody can
    // see — but post-cutover it would fire constantly and bury real faults.
    //
    // So: strip the id and hide it NOW (a fresh container can be minted
    // immediately, which is what the id is for), and delete the node only after
    // the animation window has passed.
    stale.removeAttribute('id');
    stale.style.display = 'none';
    const doomed = stale;
    setTimeout(() => doomed.remove(), 1000);
  }, [containerId]);

  /**
   * Create a fresh container inside the host's stable wrapper and return its id.
   *
   * ⚠️ THE HOST RENDERS A WRAPPER, NOT THE CONTAINER ITSELF. React owns its own
   * DOM: replacing a React-rendered node behind its back invites a
   * reconciliation conflict (`removeChild` on a node React no longer sees), and
   * React 18 StrictMode double-mounts make that worse. So the component renders
   * `<div id="{containerId}-host" />` and never touches what is inside it; this
   * hook creates and destroys the real container as a plain DOM child that
   * React has no knowledge of.
   */
  const prepareContainer = useCallback((): string => {
    teardownVerifier();
    const host = document.getElementById(`${containerId}-host`);
    // If the host is missing the ceremony cannot proceed, but returning the id
    // lets Firebase raise its own clear error rather than us throwing here.
    if (!host) return containerId;
    const fresh = document.createElement('div');
    fresh.id = containerId;
    host.appendChild(fresh);
    return containerId;
  }, [containerId, teardownVerifier]);

  // ⚠️ NOT IN THE CONSOLE. A `RecaptchaVerifier` holds a DOM widget and an IdP
  // token; unmounting the enrollment panel without clearing it leaks both, and
  // a remount then renders a second widget into the same container. The console
  // has no unmount cleanup and should gain one.
  useEffect(() => {
    return () => {
      teardownVerifier();
    };
  }, [teardownVerifier]);

  /**
   * Back to the phone step, authorization intact.
   *
   * ⚠️ Clears the verification id — the OTP that was dispatched belongs to the
   * OLD number and must not be usable against a new one. The latch is left
   * ARMED on purpose: the mint is still valid and still bound to this uid, so
   * re-minting would be waste and would count against the limiter.
   *
   * The cooldown is deliberately NOT reset either. It throttles SMS dispatch,
   * and stepping back is not a reason to hand out a free send.
   */
  const changeNumber = useCallback(() => {
    verificationIdRef.current = null;
    teardownVerifier();
    setError(null);
    setStep('phone');
  }, [teardownVerifier]);

  const reset = useCallback(() => {
    authorizationMintedRef.current = false;
    verificationIdRef.current = null;
    teardownVerifier();
    sendCountRef.current = 0;
    // A fresh ceremony gets a fresh cap, so reaching it again is reportable.
    capReportedRef.current = false;
    setResendAllowedAt(0);
    setError(null);
    setRegionWarning(false);
    setStep('reauth');
  }, [teardownVerifier]);

  /**
   * Layer 1, on blur — advisory only, never a gate.
   *
   * ⚠️ Editing the number INVALIDATES the previous attempt's verdict. Without
   * clearing here the error is only reset at the next submit, so a rejection
   * stays on screen while the patient types a corrected number — it reads as
   * frozen, and they conclude the new number failed too. Measured on-device
   * 2026-07-29: an `invalid-phone` from a typo persisted across a valid US
   * number and looked like "US numbers stopped working".
   */
  const clearError = useCallback(() => setError(null), []);

  const checkRegion = useCallback((phone: string, country?: string | null) => {
    setError(null);
    // Parse against the SELECTED country (CU-86ajrx60c) so a locally-dialled
    // number resolves and reaches the region check.
    const e164 = formatPhoneE164(phone, country);
    // Only advise once the number is complete enough to name a country —
    // otherwise every half-typed number flashes a warning.
    setRegionWarning(e164 !== null && !isRegionAllowed(e164));
  }, []);

  /**
   * Step 1 — the M-E step-up, then THE CHECKPOINT.
   *
   * ⚠️ FAIL-CLOSED, AND THIS IS THE WHOLE TASK. If the authorization cannot be
   * minted we do NOT advance: `setStep('phone')` is the last statement on the
   * success path, so any throw from the mint lands in the catch below and the
   * ceremony stays on 'reauth'. Continuing would knowingly create an enrollment
   * that the reconciliation sweep reads as a plant.
   */
  const submitPassword = useCallback(
    async (password: string): Promise<boolean> => {
      if (busy) return false;
      setBusy(true);
      setError(null);
      try {
        const user = auth.currentUser;
        if (!user?.email) {
          // Enrollment is only reachable from an authenticated screen, so this
          // is defensive rather than an expected path.
          setError({ kind: 'generic' });
          return false;
        }

        // ── M-E step-up: EXPLICIT reauthentication, right now ──────────────
        try {
          if (deps.reauthenticate) {
            await deps.reauthenticate(password);
          } else {
            // Web uses the STANDALONE modular form. (Mobile deliberately uses
            // the instance method because the standalone misbehaved on RNFB
            // v22 — that is an RNFB bug, not a web one.)
            await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
          }
        } catch (reauthError) {
          const code = (reauthError as { code?: string })?.code ?? '';
          // ⚠️ THIS BRANCH MUST BE CHECKED, AND THE PASSWORD WAS CORRECT.
          // Identity Platform raises `auth/multi-factor-auth-required` from
          // reauthentication when the account already holds a factor. Treating
          // it as an unknown fault renders "something went wrong" over a
          // perfectly good password — measured on staging 2026-08-03.
          const isAlreadyEnrolled = code === 'auth/multi-factor-auth-required';
          const isBadPassword = code === 'auth/wrong-password' || code === 'auth/invalid-credential';
          const isStaleSession = code === 'auth/requires-recent-login';
          const isThrottled = code === 'auth/too-many-requests';
          // ⚠️ After the exact codes, before the `generic` fallback
          // (CU-86akbg4qm). Held rather than captured: a capture made offline
          // is discarded by the transport.
          const isOffline =
            !isAlreadyEnrolled &&
            !isBadPassword &&
            !isStaleSession &&
            !isThrottled &&
            isNetworkError(code, (reauthError as { message?: string })?.message);
          // A wrong password, a stale session, a throttle and an
          // already-enrolled account are ordinary user-facing outcomes with
          // their own copy — breadcrumb, not exception. Anything else is a real
          // fault we must SEE.
          if (isOffline) {
            noteOfflineFailure({ feature: 'mfa-sms', step: 'enroll-reauth', code, classified: true });
          } else if (!isBadPassword && !isStaleSession && !isThrottled && !isAlreadyEnrolled) {
            Sentry.captureException(reauthError, {
              tags: { feature: 'mfa-sms', step: 'enroll-reauth', auth_error_code: scrubSafeAuthCode(code) },
            });
            if (!browserReportsOnline()) {
              noteOfflineFailure({ feature: 'mfa-sms', step: 'enroll-reauth', code, classified: false });
            }
          } else {
            Sentry.addBreadcrumb({
              category: 'mfa-sms',
              level: 'warning',
              message: 'enrollment step-up: password rejected',
              data: { code: scrubSafeAuthCode(code) },
            });
          }
          setError(
            isAlreadyEnrolled
              ? { kind: 'already-enrolled' }
              : isBadPassword
                ? { kind: 'bad-password' }
                : isStaleSession
                  ? { kind: 'stale-session' }
                  : isThrottled
                    ? { kind: 'too-many-requests' }
                    : isOffline
                      ? { kind: 'offline' }
                      : { kind: 'generic' },
          );
          return false;
        }

        // The reauth just advanced `auth_time`, so force a refresh to get a
        // token carrying it. THIS is the proof the server validates.
        const reauthProof = deps.getReauthProof ? await deps.getReauthProof() : await user.getIdToken(true);

        // ⚠️ THE CHECKPOINT. Everything below this line depends on it, and
        // nothing below it runs if this throws.
        if (deps.mintAuthorization) {
          await deps.mintAuthorization(reauthProof);
        } else {
          await requestEnrollAuthorization(reauthProof);
        }

        // ⚠️ LINK 3 — set ONLY after the mint has actually resolved. THIS
        // ordering is the load-bearing one: an optimistic set before the
        // `await` leaves the latch armed when the mint fails, and `submitPhone`
        // then sails through its own latch check and DISPATCHES A REAL OTP with
        // no authorization behind it. Measured by mutating this file 2026-08-07
        // — the step machine held at 'reauth' and every other rail test still
        // passed, so nothing but a latch-specific test catches it.
        authorizationMintedRef.current = true;
        // Reached the server, so the network is back — report anything held.
        // ⚠️ AFTER the awaited mint like everything else here; it has no bearing
        // on the rail, it only reads module state.
        flushOfflineFailures('success');
        // ⚠️ LINK 1. Note these two statements are ORDER-INDEPENDENT relative to
        // each other — the mint throws before either runs, so swapping them
        // changes nothing observable (verified by mutation). The console's
        // comment implies `setStep` being last is itself the safeguard; it is
        // not. What matters is that BOTH sit after the awaited mint.
        setStep('phone');
        return true;
      } catch (err) {
        const code = (err as { code?: string })?.code ?? '';
        const message = (err as { message?: string })?.message ?? '';
        // The server rejects a proof older than REAUTH_MAX_AGE_S (120s) with
        // failed-precondition/"reauth-required". Same remedy as the client-side
        // stale case: ask for the password again.
        //
        // ⚠️ This is NOT hypothetical. The proof is minted at reauth — before
        // the patient has typed a number or waited on an SMS — so a slow
        // ceremony ages it out routinely.
        const isStale =
          code === 'auth/requires-recent-login' ||
          code === 'functions/failed-precondition' ||
          message.includes('reauth-required');
        // The checkpoint's own fail-closed limiter: 5 per 300s, keyed on uid.
        // ⚠️ Classified distinctly because "try again in a moment" is WRONG for
        // a 300-second window.
        const isThrottled = code === 'functions/resource-exhausted' || code === 'auth/too-many-requests';
        // ⚠️ THIS CATCH SPANS TWO SDKs: the forced token refresh (Auth →
        // `auth/network-request-failed`) and the mint (Functions →
        // `functions/internal`, MEASURED probe 4b). The predicate handles both,
        // and holds EVERY transport code to `navigator.onLine`, because a
        // reached-but-failing server produces the very same codes.
        const isOffline = !isStale && !isThrottled && isNetworkError(code, message);

        if (isOffline) {
          // Held, not captured — a capture made offline is discarded.
          noteOfflineFailure({ feature: 'mfa-sms', step: 'enroll-authorization', code, classified: true });
        } else if (isStale || isThrottled) {
          Sentry.addBreadcrumb({
            category: 'mfa-sms',
            level: 'warning',
            message: 'enrollment: authorization mint refused',
            data: { code: scrubSafeAuthCode(code) },
          });
        } else {
          // A mint failure means the patient CANNOT enrol without tripping the
          // detector, so it must never be silent.
          Sentry.captureException(err, {
            tags: { feature: 'mfa-sms', step: 'enroll-authorization', auth_error_code: scrubSafeAuthCode(code) },
            extra: { message: message || '(none)' },
          });
          if (!browserReportsOnline()) {
            noteOfflineFailure({ feature: 'mfa-sms', step: 'enroll-authorization', code, classified: false });
          }
        }
        setError(
          isStale
            ? { kind: 'stale-session' }
            : isThrottled
              ? { kind: 'too-many-requests' }
              : isOffline
                ? { kind: 'offline' }
                : { kind: 'generic' },
        );
        return false;
      } finally {
        setBusy(false);
      }
    },
    [busy, deps],
  );

  /** Step 2 — send the OTP to the SUBMITTED number. */
  const submitPhone = useCallback(
    async (phone: string, country?: string | null): Promise<boolean> => {
      if (busy) return false;

      // ⚠️ Defence in depth: this step is only reachable at step 'phone', which
      // only the mint's success path sets — but assert the latch anyway so a
      // reordering refactor cannot quietly open a path around the checkpoint.
      if (!authorizationMintedRef.current) {
        setError({ kind: 'generic' });
        return false;
      }

      if (sendCountRef.current >= RESEND_CAP) {
        // Reaching the cap is genuinely actionable — either SMS is not reaching
        // a real patient, or someone is pumping messages at a phone. NO PHI:
        // only counts, never the number.
        //
        // ⚠️ UNREACHABLE THROUGH THE UI until CU-86ajtgauu: both send buttons
        // were disabled at the cap, and they are the only callers.
        if (!capReportedRef.current) {
          capReportedRef.current = true;
          Sentry.captureMessage('mfa enrollment: resend cap reached', {
            level: 'warning',
            tags: { feature: 'mfa-sms', step: 'enroll-resend-cap' },
            extra: { resendCap: RESEND_CAP, otpSendCount: sendCountRef.current },
          });
        }
        setError({ kind: 'resend-cap-reached' });
        return false;
      }
      if (now() < resendAllowedAt) {
        // The button is disabled and counting down, so the patient can already
        // see why. Refuse silently rather than scolding them.
        return false;
      }

      setBusy(true);
      setError(null);
      try {
        // Parse against the SELECTED country (CU-86ajrx60c). The console's
        // North-America-only normalizer would reject a locally-dialled AU
        // number as malformed before it ever reached the region check.
        const e164 = formatPhoneE164(phone, country);
        if (!e164) {
          setError({ kind: 'invalid-phone' });
          return false;
        }

        // ── LAYER 1 — advisory, pre-submission. Saves the patient waiting on
        // an SMS that will never arrive. It never GRANTS anything: an
        // over-narrow mirror routes an eligible patient to friendly copy, and
        // an over-broad one simply defers to Layer 2 below.
        if (!isRegionAllowed(e164)) {
          setError({ kind: 'region-not-supported' });
          return false;
        }

        const user = auth.currentUser;
        if (!user) {
          setError({ kind: 'generic' });
          return false;
        }

        // ── Preflight: verified email ──────────────────────────────────────
        // IdP refuses enrollment for unverified emails, so calling the ceremony
        // would burn a round-trip to be told no. Check locally instead.
        //
        // ⚠️ reload() FIRST: `emailVerified` on the cached user object is a
        // SNAPSHOT from token-mint time. A patient who verifies in their inbox
        // and comes straight back still carries a stale `false` — blocking them
        // on it would be a self-inflicted dead end.
        //
        // ⚠️ A reload fault must NOT block enrollment: fall through and let the
        // server be the authority (it always was — this preflight is an
        // optimisation and a copy improvement, never the security boundary).
        try {
          if (deps.reloadUser) {
            await deps.reloadUser();
          } else {
            await user.reload();
          }
        } catch (reloadError) {
          Sentry.addBreadcrumb({
            category: 'mfa-sms',
            level: 'warning',
            message: 'enrollment: user.reload() failed, proceeding to server',
            data: { code: scrubSafeAuthCode((reloadError as { code?: string })?.code) },
          });
        }

        const verified = deps.isEmailVerified ? deps.isEmailVerified() : auth.currentUser?.emailVerified !== false;
        if (!verified) {
          setError({ kind: 'unverified-email' });
          return false;
        }

        const session = deps.getSession ? await deps.getSession() : await multiFactor(user).getSession();

        // A verifier is single-use. ⚠️ `prepareContainer` tears down the old one
        // AND mints a brand-new container element — see teardownVerifier for
        // why emptying the existing one is not enough for an INVISIBLE verifier.
        const freshContainerId = prepareContainer();
        const verifier = deps.makeVerifier
          ? deps.makeVerifier(freshContainerId)
          : new RecaptchaVerifier(auth, freshContainerId, { size: 'invisible' });
        verifierRef.current = verifier;

        const verify =
          deps.verifyPhoneNumber ??
          ((options: unknown, v: unknown) => new PhoneAuthProvider(auth).verifyPhoneNumber(options as never, v as never));

        // ⚠️ WEB SHAPE: options object + verifier. NOT mobile's positional args
        // — `verifyPhoneNumberWithMultiFactorInfo` does not exist in the JS SDK.
        const verificationId = await verify({ phoneNumber: e164, session }, verifier);

        verificationIdRef.current = verificationId;
        sendCountRef.current += 1;

        // Escalate the next wait, indexed by resends already made, so the first
        // resend waits 30s and the third and beyond 120s. Armed only AFTER a
        // send succeeds — a failed dispatch must not lock out a retry.
        const resendsMade = sendCountRef.current - 1;
        setResendAllowedAt(now() + RESEND_COOLDOWNS_MS[Math.min(resendsMade, RESEND_COOLDOWNS_MS.length - 1)]);

        flushOfflineFailures('success');
        setStep('code');
        return true;
      } catch (err) {
        const code = (err as { code?: string })?.code ?? '';
        const message = (err as { message?: string })?.message ?? '';

        // ⚠️ ORDER MATTERS HERE. The verified-email gate is checked FIRST and on
        // the MESSAGE, not the code: the backend reports it as `auth/unknown`
        // with the only usable signal in the prose. Classifying on code alone
        // falls through to `generic` — "try again in a moment", advice that can
        // NEVER work until the inbox is actioned.
        const isUnverifiedEmail = isUnverifiedEmailRejection(code, message);
        // ── LAYER 2 — the correctness boundary. The client mirror WILL drift
        // from the hand-edited console allowlist, so a number Layer 1 allowed
        // can still be rejected here, and it must render the SAME copy.
        //
        // ⚠️ `isRegionRejection` requires the MESSAGE, not just the code:
        // `auth/operation-not-allowed` also means "phone provider disabled
        // project-wide" — a TOTAL SMS OUTAGE that must reach `send-failed` →
        // captureException rather than being shown to a patient as a region
        // problem. See utils/mfa/regionPolicy.ts.
        const isRegion = isRegionRejection(code, message);
        const isMalformed = isMalformedPhoneError(code) || code === 'auth/missing-phone-number';
        const isThrottled = code === 'auth/too-many-requests';
        const isStale = code === 'auth/requires-recent-login';
        // ⚠️ LAST before the `send-failed` catch-all (CU-86akbg4qm), which would
        // otherwise swallow it. `auth/internal-error` is offline ONLY with the
        // browser offline (the first send, before reCAPTCHA ever loaded —
        // measured). A reCAPTCHA blocked while ONLINE (probe 3) stays
        // `send-failed` and captured: reconnecting will not help there.
        const isOffline = isNetworkError(code, message);

        const kind: SmsEnrollmentError['kind'] = isUnverifiedEmail
          ? 'unverified-email'
          : isRegion
            ? 'region-not-supported'
            : isMalformed
              ? 'invalid-phone'
              : isThrottled
                ? 'too-many-requests'
                : isStale
                  ? 'stale-session'
                  : isOffline
                    ? 'offline'
                    : 'send-failed';

        // `send-failed` is the classifier's fallback here, so it covers the
        // unrecognised case that `generic` handles on other steps. Captured
        // deliberately rather than breadcrumbed: a total SMS outage would block
        // every patient from ever reaching AAL2, and it must not hide.
        if (kind === 'offline') {
          // Held, not captured — a capture made offline is discarded.
          noteOfflineFailure({ feature: 'mfa-sms', step: 'enroll-send-otp', code, classified: true });
        } else if (kind === 'send-failed') {
          Sentry.captureException(err, {
            tags: {
              feature: 'mfa-sms',
              step: 'enroll-send-otp',
              // ⚠️ A TAG, not `extra` (CU-86akbcqv8) — see `sentryAuthCode.ts`.
              auth_error_code: scrubSafeAuthCode(code),
            },
            // NEVER the phone number (M3). ⚠️ `message` stays in `extra` and
            // stays SCRUBBED by design: Firebase prose contains `auth/…`, and
            // exempting a free-text field from every default keyword is the
            // widest grant available. The code above is the readable signal.
            extra: { message: message || '(none)' },
          });
          if (!browserReportsOnline()) {
            noteOfflineFailure({ feature: 'mfa-sms', step: 'enroll-send-otp', code, classified: false });
          }
        } else {
          Sentry.addBreadcrumb({
            category: 'mfa-sms',
            level: 'warning',
            message: `enrollment: send rejected (${kind})`,
            data: { code: scrubSafeAuthCode(code) },
          });
        }
        if (kind === 'stale-session') setStep('reauth');
        setError({ kind });
        return false;
      } finally {
        setBusy(false);
      }
    },
    [busy, deps, now, prepareContainer, resendAllowedAt],
  );

  /**
   * Re-send the account verification email.
   *
   * ⚠️ DELIBERATELY DOES NOT CLEAR `error`. The `unverified-email` error carries
   * the instruction the patient still needs ("check your inbox, then come
   * back"); clearing it on send would erase that context at the exact moment
   * they need it. The boolean return lets the UI show a transient
   * "couldn't send" alongside the standing message instead.
   */
  const resendVerificationEmail = useCallback(async (): Promise<boolean> => {
    const user = auth.currentUser;
    if (!user) return false;
    try {
      if (deps.sendVerificationEmail) {
        await deps.sendVerificationEmail();
      } else {
        await sendEmailVerification(user);
      }
      Sentry.addBreadcrumb({
        category: 'mfa-sms',
        level: 'info',
        message: 'enrollment: verification email re-sent',
      });
      return true;
    } catch (err) {
      const code = (err as { code?: string })?.code ?? '';
      // Throttling here is ordinary — Firebase rate-limits verification sends,
      // and a patient clicking twice is not a fault.
      if (code === 'auth/too-many-requests') {
        Sentry.addBreadcrumb({
          category: 'mfa-sms',
          level: 'warning',
          message: 'enrollment: verification email throttled',
          data: { code: scrubSafeAuthCode(code) },
        });
      } else {
        // Anything else means the patient's ONLY route out of the unverified
        // state is broken, and the console cannot send one for them.
        Sentry.captureException(err, {
          tags: { feature: 'mfa-sms', step: 'enroll-verification-email', auth_error_code: scrubSafeAuthCode(code) },
        });
      }
      return false;
    }
  }, [deps]);

  /** Step 3 — prove possession, then enroll the factor. */
  const submitCode = useCallback(
    async (code: string): Promise<boolean> => {
      if (busy) return false;

      // ⚠️ THE RAIL, ASSERTED AT THE POINT OF NO RETURN. `enroll()` is
      // irreversible from the client's perspective and is the exact call that
      // creates a factor at Identity Platform. If no authorization was minted,
      // enrolling here would manufacture precisely the unmatched factor this
      // feature exists to prevent — so refuse, loudly.
      if (!authorizationMintedRef.current) {
        Sentry.captureMessage('mfa enrollment: enroll blocked — no authorization minted', {
          level: 'error',
          tags: { feature: 'mfa-sms', step: 'enroll-rail' },
        });
        setError({ kind: 'generic' });
        return false;
      }

      const verificationId = verificationIdRef.current;
      if (!verificationId) {
        setError({ kind: 'generic' });
        return false;
      }

      setBusy(true);
      setError(null);
      try {
        const user = auth.currentUser;
        if (!user) {
          setError({ kind: 'generic' });
          return false;
        }

        const assertion = deps.buildAssertion
          ? deps.buildAssertion(verificationId, code)
          : PhoneMultiFactorGenerator.assertion(PhoneAuthProvider.credential(verificationId, code));

        if (deps.enroll) {
          await deps.enroll(assertion, FACTOR_DISPLAY_NAME);
        } else {
          await multiFactor(user).enroll(assertion as never, FACTOR_DISPLAY_NAME);
        }

        // ── The factor is LIVE at IdP from this point ─────────────────────
        // ⚠️ Enrollment REVOKES existing refresh tokens: any other session this
        // patient has open is signed out. The UI must say so.
        //
        // ⚠️ AAL2 is granted WITHOUT re-login — measured on the console. The
        // enroll call updates the session token to carry
        // `firebase.sign_in_second_factor`, so the patient can continue
        // immediately.
        flushOfflineFailures('success');
        setStep('done');
        return true;
      } catch (err) {
        const errorCode = (err as { code?: string })?.code ?? '';
        // A wrong or expired code is ordinary user error — the patient retries
        // and is never trapped. Everything else is a real fault we must see.
        const isBadCode =
          errorCode === 'auth/invalid-verification-code' ||
          errorCode === 'auth/invalid-verification-id' ||
          errorCode === 'auth/code-expired';
        const isThrottled = errorCode === 'auth/too-many-requests';
        // After the exact codes, before `generic` (CU-86akbg4qm).
        const isOffline = !isBadCode && !isThrottled && isNetworkError(errorCode, (err as { message?: string })?.message);
        if (isOffline) {
          // Held, not captured — a capture made offline is discarded.
          noteOfflineFailure({ feature: 'mfa-sms', step: 'enroll-finalize', code: errorCode, classified: true });
        } else if (!isBadCode && !isThrottled) {
          Sentry.captureException(err, {
            tags: {
              feature: 'mfa-sms',
              step: 'enroll-finalize',
              // ⚠️ A TAG, not `extra` (CU-86akbcqv8) — see `sentryAuthCode.ts`.
              auth_error_code: scrubSafeAuthCode(errorCode),
            },
            // NEVER the phone number, the OTP, or the verification id — the
            // last two are credentials.
          });
          if (!browserReportsOnline()) {
            noteOfflineFailure({ feature: 'mfa-sms', step: 'enroll-finalize', code: errorCode, classified: false });
          }
        } else {
          Sentry.addBreadcrumb({
            category: 'mfa-sms',
            level: 'warning',
            message: 'enrollment: verification code rejected',
            data: { code: scrubSafeAuthCode(errorCode) },
          });
        }
        setError(
          isBadCode
            ? { kind: 'bad-code' }
            : isThrottled
              ? { kind: 'too-many-requests' }
              : isOffline
                ? { kind: 'offline' }
                : { kind: 'generic' },
        );
        return false;
      } finally {
        setBusy(false);
      }
    },
    [busy, deps],
  );

  return {
    step,
    busy,
    error,
    regionWarning,
    submitPassword,
    submitPhone,
    submitCode,
    resendVerificationEmail,
    clearError,
    checkRegion,
    changeNumber,
    reset,
    resendCooldownSecs,
    resendCapReached,
    /**
     * ⚠️ Clears the latch WITHOUT clearing the verification id, reproducing the
     * one state that isolates the rail: a live dispatched OTP with no minted
     * authorization behind it — exactly what a reordering refactor produces.
     *
     * `reset()` clears BOTH, so a rail test built on it passes even when the
     * latch is deleted, proving nothing. The console found that false pass by
     * mutation-testing the file. Do not "simplify" this away.
     */
    __clearAuthorizationLatchForTest: () => {
      authorizationMintedRef.current = false;
    },
  };
}
